import ModalAnimator from '@root/joinroot.com/src/components/modal/_modal-animator';
import PropTypes from '@root/vendor/prop-types';
import React, { useLayoutEffect, useRef } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import ZIndex from '@root/core/src/utils/z-index';
import closeButtonIcon from '@root/core/src/assets/closeModal.svg';
import closeCircleIcon from '@root/core/src/assets/closeCircle.svg';
import useModal from '@root/core/src/hooks/use-modal';
import usePortal from '@root/core/src/hooks/use-portal';
import { Colors, StyleSheet } from '@root/core/src/utils/styles';

const isBrowser = typeof window !== 'undefined';

export default function Modal({
  children,
  name,
  isShowing,
  onClose,
  renderCloseButton = true,
}) {
  const portalRef = useRef();

  const renderPortal = usePortal(name);
  useModal(onClose, portalRef);

  useLayoutEffect(() => {
    if (!isBrowser) { return; }

    isShowing && (document.documentElement.style.overflow = 'hidden');
    return () => document.documentElement.style.overflow = '';
  }, [isShowing]);

  if (!isBrowser) { return null; }

  return renderPortal(
    <ModalAnimator>
      {isShowing && (
        <div
          css={styles.cardWrapper}
          data-testid={`${name}-wrapper`}
        >
          <div
            css={styles.outerContent}
            ref={portalRef}
          >
            {renderCloseButton &&
              <button
                aria-label={'close-button'}
                css={styles.closeButton}
                onClick={onClose}
              >
                <img
                  className={'desktop-icon'}
                  src={closeButtonIcon}
                />
                <img
                  className={'mobile-icon'}
                  src={closeCircleIcon}
                />
              </button>
            }
            <div css={styles.innerContent}>
              {children}
            </div>
          </div>
        </div>
      )}
    </ModalAnimator>
  );
}

Modal.propTypes = {
  children: PropTypes.node,
  isShowing: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  renderCloseButton: PropTypes.bool,
};

const styles = StyleSheet.create({
  cardWrapper: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    zIndex: ZIndex.MODAL,
    ...Responsive.sm({
      alignItems: 'center',
    }),
  },
  outerContent: {
    backgroundColor: Colors.white(),
    padding: 20,
    borderRadius: '20px 20px 0 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxHeight: '88vh',
    width: '100vw',
    ...Responsive.sm({
      padding: 14,
      borderRadius: 10,
      width: 'auto',
      maxHeight: '80vh',
    }),
  },
  innerContent: {
    maxWidth: 400,
    maxHeight: '100vh',
    padding: 10,
    overflow: 'auto',
    ...Responsive.sm({
      padding: 30,
    }),
  },
  closeButton: {
    position: 'absolute',
    background: 'none',
    padding: 0,
    border: 'none',
    cursor: 'pointer',
    width: 64,
    height: 64,
    transform: 'translateY(-95px)',
    '& .desktop-icon': {
      display: 'none',
    },
    '& .mobile-icon': {
      display: 'initial',
    },
    ...Responsive.sm({
      alignSelf: 'flex-end',
      width: 24,
      height: 24,
      transform: 'none',
      '& .desktop-icon': {
        display: 'initial',
      },
      '& .mobile-icon': {
        display: 'none',
      },
    }),
  },
});
